/**
 * variant-media.js
 * Shows the selected variant's image:
 *  - product page: scrolls the media gallery to the variant's media
 *  - product cards: swaps the card image; variant images are prefetched on hover/focus/touch
 */
const CARD_WIDTHS = [300, 400, 600, 800];
const MAX_PRELOADS = 60;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

const variantCache = new WeakMap(); // JSON <script> -> parsed variants
const originals = new WeakMap(); // card <img> -> original product image
const pending = new WeakMap(); // card <img> -> latest requested src
const preloads = new Map(); // srcset -> decode promise (shared by prefetch and click)
let lastWarmed = null;

/* ---------- shared ---------- */

function getVariants(jsonEl) {
  if (!jsonEl) return [];
  let list = variantCache.get(jsonEl);
  if (!list) {
    try {
      const data = JSON.parse(jsonEl.textContent || '[]');
      list = Array.isArray(data) ? data.filter((v) => Array.isArray(v?.options)) : [];
    } catch {
      list = []; // malformed JSON: feature disabled, page unaffected
    }
    variantCache.set(jsonEl, list);
  }
  return list;
}

// Empty slots in `selected` act as wildcards.
function findVariant(variants, selected) {
  return variants.find((v) => selected.every((val, i) => val == null || v.options[i] === val));
}

/* ---------- product page gallery ---------- */

function syncGallery(picker, animate) {
  const selected = Array.from(
    picker.querySelectorAll('fieldset.variant-option-group'),
    (fs) => fs.querySelector('input:checked')?.value
  );
  const variant = findVariant(getVariants(picker.querySelector('script[type="application/json"]')), selected);
  const id = variant?.featured_media?.id;
  if (!Number.isSafeInteger(id)) return;

  const scope = picker.closest('.shopify-section') ?? document;
  for (const slide of scope.querySelectorAll('.gallery-main > [data-media-id]')) {
    if (slide.dataset.mediaId !== String(id)) continue;

    const track = slide.parentElement;
    const dx = slide.getBoundingClientRect().left - track.getBoundingClientRect().left;
    if (Math.abs(dx) > 1) {
      track.scrollBy({ left: dx, behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto' });
    }
    return;
  }
}

/* ---------- product cards ---------- */

// Only accept https images from Shopify's CDN or the host the card image already uses.
function toImageUrl(src, img) {
  try {
    const base = `https://${location.host}/`;
    const url = new URL(src, base);
    const host = new URL(img.currentSrc || img.src, base).hostname;
    return url.protocol === 'https:' && (url.hostname === host || url.hostname === 'cdn.shopify.com') ? url : null;
  } catch {
    return null;
  }
}

function sized(url, width) {
  const u = new URL(url);
  u.searchParams.set('width', width);
  return u.href.replace(/,/g, '%2C'); // keep srcset parsing safe
}

// Works out which image a pill would show: active pills + this one.
function resolveCardImage(btn) {
  const card = btn.closest('product-card-wrapper');
  const img = card?.querySelector('img.product-main-image');
  if (!img) return null;

  const selected = [];
  for (const b of [...card.querySelectorAll('.variant-option-btn.active'), btn]) {
    const i = Number(b.dataset.variantOption) - 1;
    if (i >= 0 && i < 3) selected[i] = b.dataset.optionValue;
  }
  const variant = findVariant(getVariants(card.querySelector('script[data-product-variants]')), selected);
  if (!variant) return null;

  if (!originals.has(img)) originals.set(img, { src: img.src, srcset: img.srcset, alt: img.alt });

  const fi = variant.featured_image;
  if (!fi?.src) return { img, next: originals.get(img) }; // variant without its own image -> product image

  const base = toImageUrl(fi.src, img);
  if (!base) return null;
  return {
    img,
    next: {
      src: sized(base, 600),
      srcset: CARD_WIDTHS.map((w) => `${sized(base, w)} ${w}w`).join(', '),
      alt: fi.alt || originals.get(img).alt,
    },
  };
}

// Loads + decodes once; prefetch and click share the same promise.
function preload(next, sizes, priority) {
  let p = preloads.get(next.srcset);
  if (!p) {
    const pre = new Image();
    pre.fetchPriority = priority;
    pre.sizes = sizes;
    pre.srcset = next.srcset;
    pre.src = next.src;
    p = pre.decode();
    p.catch(() => preloads.delete(next.srcset)); // allow a retry after a failed load
    preloads.set(next.srcset, p);
    if (preloads.size > MAX_PRELOADS) preloads.delete(preloads.keys().next().value);
  }
  return p;
}

async function showCardImage(img, next) {
  pending.set(img, next.src);
  if (img.src === next.src) return;

  try {
    await preload(next, img.sizes, 'high');
  } catch {
    return; // failed load: keep the current image
  }
  if (pending.get(img) !== next.src) return; // a newer selection won

  img.srcset = next.srcset;
  img.src = next.src;
  img.alt = next.alt;
}

/* ---------- listeners ---------- */

document.addEventListener('change', (e) => {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('variant-option-input')) return;
  const picker = input.closest('variant-radios');
  if (picker) syncGallery(picker, true);
});

// Capture phase: runs before the card's own handler, so its stopPropagation() can't block us.
document.addEventListener(
  'click',
  (e) => {
    const btn = e.target instanceof Element ? e.target.closest('.variant-option-btn') : null;
    if (!btn || btn.disabled) return;
    const r = resolveCardImage(btn);
    if (r) showCardImage(r.img, r.next);
  },
  { capture: true }
);

// Prefetch on intent (hover, keyboard focus, touch start) so the image is ready by the click.
function warm(e) {
  const btn = e.target instanceof Element ? e.target.closest('.variant-option-btn') : null;
  if (!btn || btn === lastWarmed || btn.disabled) return;
  lastWarmed = btn;
  const r = resolveCardImage(btn);
  if (r && r.img.src !== r.next.src) preload(r.next, r.img.sizes, 'low').catch(() => {});
}
document.addEventListener('pointerover', warm, { passive: true });
document.addEventListener('focusin', warm);

// Deep links (?variant=..., e.g. Google Shopping): align the product gallery on load.
if (new URLSearchParams(location.search).has('variant')) {
  document.querySelectorAll('variant-radios').forEach((picker) => syncGallery(picker, false));
}